import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RecurrenceType, ScheduleWeekDay } from "@/types/staff-schedule";
import {
  createScheduleFormSchema,
  ScheduleFormData,
} from "../utils/scheduleSchema";

function getSubmitLabel(
  isSubmitting: boolean,
  isEdit: boolean,
  t: (key: string) => string,
): string {
  if (isSubmitting && isEdit) return t("staffSchedule.form.updating");
  if (isSubmitting) return t("staffSchedule.form.creating");
  if (isEdit) return t("staffSchedule.form.update");
  return t("staffSchedule.form.create");
}

export interface StaffOption {
  id: string;
  name: string;
}

interface ScheduleFormProps {
  staffOptions: StaffOption[];
  onSubmit: (data: ScheduleFormData) => void;
  isSubmitting: boolean;
  initialValues?: Partial<ScheduleFormData>;
  isEdit?: boolean;
}

const WEEKDAYS = Object.values(ScheduleWeekDay);

interface DayCheckboxGroupProps {
  value: ScheduleWeekDay[];
  onChange: (value: ScheduleWeekDay[]) => void;
}

const DayCheckboxGroup: React.FC<DayCheckboxGroupProps> = ({
  value,
  onChange,
}) => {
  const handleToggle = (day: ScheduleWeekDay, isChecked: boolean) => {
    onChange(isChecked ? [...value, day] : value.filter((d) => d !== day));
  };

  return (
    <div className="flex flex-wrap gap-3">
      {WEEKDAYS.map((day) => (
        <label key={day} className="flex cursor-pointer items-center gap-1.5">
          <Checkbox
            checked={value.includes(day)}
            onCheckedChange={(isChecked) =>
              handleToggle(day, isChecked as boolean)
            }
          />
          <span className="text-sm">{day}</span>
        </label>
      ))}
    </div>
  );
};

const RECURRENCE_TYPE_LABELS: Record<RecurrenceType, string> = {
  [RecurrenceType.EVERY_N_DAYS]: "staffSchedule.form.everyNDays",
  [RecurrenceType.WEEKLY]: "staffSchedule.form.weekly",
  [RecurrenceType.BIWEEKLY]: "staffSchedule.form.biweekly",
};

export const ScheduleForm: React.FC<ScheduleFormProps> = ({
  staffOptions,
  onSubmit,
  isSubmitting,
  initialValues,
  isEdit = false,
}) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(createScheduleFormSchema()),
    defaultValues: {
      staffId: "",
      recurrenceType: RecurrenceType.WEEKLY,
      recurrenceInterval: undefined,
      days: [],
      anchorDate: "",
      startTime: "",
      endTime: "",
      effectiveFrom: "",
      effectiveTo: "",
      ...initialValues,
    },
  });

  useEffect(() => {
    if (initialValues) {
      reset({
        staffId: "",
        recurrenceType: RecurrenceType.WEEKLY,
        days: [],
        anchorDate: "",
        startTime: "",
        endTime: "",
        effectiveFrom: "",
        effectiveTo: "",
        ...initialValues,
      });
    }
  }, [initialValues, reset]);

  const recurrenceType = watch("recurrenceType");
  const showInterval = recurrenceType === RecurrenceType.EVERY_N_DAYS;
  const showDays =
    recurrenceType === RecurrenceType.WEEKLY ||
    recurrenceType === RecurrenceType.BIWEEKLY;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="staffId">{t("staffSchedule.form.staffMember")} *</Label>
        <Controller
          name="staffId"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={field.onChange}
              disabled={isEdit}
            >
              <SelectTrigger className="w-full justify-between">
                <SelectValue
                  placeholder={t("staffSchedule.form.staffMemberPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {staffOptions.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        {errors.staffId && (
          <p className="text-destructive text-sm">{errors.staffId.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>{t("staffSchedule.form.recurrenceType")} *</Label>
        <Controller
          name="recurrenceType"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full justify-between">
                <SelectValue
                  placeholder={t(
                    "staffSchedule.form.recurrenceTypePlaceholder",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(RecurrenceType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(RECURRENCE_TYPE_LABELS[type])}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        {errors.recurrenceType && (
          <p className="text-destructive text-sm">
            {errors.recurrenceType.message}
          </p>
        )}
      </div>

      {showInterval && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recurrenceInterval">
            {t("staffSchedule.form.recurrenceInterval")} *
          </Label>
          <Input
            id="recurrenceInterval"
            type="number"
            min={1}
            placeholder={t("staffSchedule.form.recurrenceIntervalPlaceholder")}
            {...register("recurrenceInterval", { valueAsNumber: true })}
          />
          {errors.recurrenceInterval && (
            <p className="text-destructive text-sm">
              {errors.recurrenceInterval.message}
            </p>
          )}
        </div>
      )}

      {showDays && (
        <div className="flex flex-col gap-1.5">
          <Label>{t("staffSchedule.form.days")} *</Label>
          <Controller
            name="days"
            control={control}
            render={({ field }) => (
              <DayCheckboxGroup
                value={field.value ?? []}
                onChange={field.onChange}
              />
            )}
          />
          {errors.days && (
            <p className="text-destructive text-sm">{errors.days.message}</p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="anchorDate">
          {t("staffSchedule.form.anchorDate")} *
        </Label>
        <Input id="anchorDate" type="date" {...register("anchorDate")} />
        {errors.anchorDate && (
          <p className="text-destructive text-sm">
            {errors.anchorDate.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="startTime">
            {t("staffSchedule.form.startTime")} *
          </Label>
          <Input id="startTime" type="time" {...register("startTime")} />
          {errors.startTime && (
            <p className="text-destructive text-sm">
              {errors.startTime.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="endTime">{t("staffSchedule.form.endTime")} *</Label>
          <Input id="endTime" type="time" {...register("endTime")} />
          {errors.endTime && (
            <p className="text-destructive text-sm">{errors.endTime.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="effectiveFrom">
            {t("staffSchedule.form.effectiveFrom")} *
          </Label>
          <Input
            id="effectiveFrom"
            type="date"
            {...register("effectiveFrom")}
          />
          {errors.effectiveFrom && (
            <p className="text-destructive text-sm">
              {errors.effectiveFrom.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="effectiveTo">
            {t("staffSchedule.form.effectiveTo")}
          </Label>
          <Input id="effectiveTo" type="date" {...register("effectiveTo")} />
          {errors.effectiveTo && (
            <p className="text-destructive text-sm">
              {errors.effectiveTo.message}
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="new_default"
        disabled={isSubmitting}
        className="w-full"
      >
        {getSubmitLabel(isSubmitting, isEdit, t)}
      </Button>
    </form>
  );
};
